class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.addEvents = this.addEvents.bind(this);

    // Lock mechanism to prevent concurrent operations (both add and remove)
    this._operationLock = false;
    this._pendingOperations = [];

    this.addEvents();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy, prepareForUpdate = false) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    
    // Check if this is the EPL cart drawer
    const isEplDrawer = this.classList.contains('epl-cart-drawer-wrapper');
    const wasEmpty = this.classList.contains('is-empty');
    const $openItems = this.querySelector('.js-cart-items-body');
    
    if (isEplDrawer && !wasEmpty && $openItems && $openItems.textContent.trim().length === 0) {
      $openItems.innerHTML = `
        <div class="epl-cart-summary" style="min-height: 200px;">
          <div style="text-align: center; padding: 40px; color: #666;">
            <p>Laster handlekurv...</p>
          </div>
        </div>
      `;
    }
    
    // If prepareForUpdate is true and cart is empty, prepare loading screen before opening
    // This prevents the empty cart state from being visible when adding the first product
    if (isEplDrawer && wasEmpty && prepareForUpdate) {
      this.prepareLoadingScreen();
    }
    
    if (isEplDrawer && !wasEmpty) {
      // Check if content is already loaded
      const $items = this.querySelector('.js-cart-items-body');
      const hasContent = $items && $items.querySelector('.epl-cart-summary') && 
                        !$items.querySelector('.epl-cart-summary').textContent.includes('Laster handlekurv');
      
      // Only refresh if content is missing or we haven't just updated (avoid double refresh)
      if (!hasContent && !this._justUpdated) {
        // Mark that we're about to open, so updateItems won't call open() again
        this._isOpening = true;
        // Fetch content immediately without showing loading screen
        this.updateItems(null, null, false); // Pass false to prevent auto-opening
        this._isOpening = false;
      }
      this._justUpdated = false;
    }
    
    // Add classes immediately for faster opening
    // Use requestAnimationFrame to ensure DOM is ready, but don't delay unnecessarily
    requestAnimationFrame(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    
    // Check if this is the EPL cart drawer and if we're transitioning from empty to non-empty
    const isEplDrawer = this.classList.contains('epl-cart-drawer-wrapper');
    const wasEmpty = this.classList.contains('is-empty');
    
    // If transitioning from empty to non-empty, immediately hide empty state and show loading
    // Do this BEFORE updating sections to prevent flicker
    if (isEplDrawer && wasEmpty) {
      const $emptyDrawer = this.querySelector('.js-empty-drawer');
      if ($emptyDrawer) {
        $emptyDrawer.classList.add('hidden');
      }
      
      const $drawerItems = this.querySelector('cart-drawer-items');
      if ($drawerItems) {
        $drawerItems.classList.remove('is-empty');
        this.classList.remove('is-empty');
        const $inner = $drawerItems.querySelector('.js-drawer-inner');
        if ($inner) {
          // Show loading screen immediately to prevent flicker
          $inner.innerHTML = `
            <div class="drawer__cart-items-wrapper js-cart-items-body">
              <div class="epl-cart-summary" style="min-height: 200px;">
                <div style="text-align: center; padding: 40px; color: #666;">
                  <p>Laster handlekurv...</p>
                </div>
              </div>
            </div>
          `;
        }
      }
    }
    
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });
    
    // After sections are updated, if we were showing loading screen, restore it
    // (in case section update overwrote it)
    if (isEplDrawer && wasEmpty) {
      const $drawerItems = this.querySelector('cart-drawer-items');
      if ($drawerItems) {
        const $inner = $drawerItems.querySelector('.js-drawer-inner');
        const $items = $inner?.querySelector('.js-cart-items-body');
        // If loading screen was overwritten or doesn't exist, restore it
        if (!$items || !$items.querySelector('.epl-cart-summary') || 
            !$items.querySelector('.epl-cart-summary').textContent.includes('Laster handlekurv')) {
          if ($inner) {
            $inner.innerHTML = `
              <div class="drawer__cart-items-wrapper js-cart-items-body">
                <div class="epl-cart-summary" style="min-height: 200px;">
                  <div style="text-align: center; padding: 40px; color: #666;">
                    <p>Laster handlekurv...</p>
                  </div>
                </div>
              </div>
            `;
          }
        }
      }
    }

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      
      // For EPL drawer, if we were transitioning from empty, call updateItems to fetch cart content
      if (isEplDrawer && wasEmpty) {
        // Extract the latest item key from parsedState if available
        const latestItemKey = parsedState.items && parsedState.items.length > 0 
          ? parsedState.items[parsedState.items.length - 1].key 
          : null;
        // Call updateItems to fetch and display the cart content
        this.updateItems(parsedState.items || null, latestItemKey, false);
      }
      
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  prepareLoadingScreen() {
    // Hide the empty drawer state immediately
    const $emptyDrawer = this.querySelector('.js-empty-drawer');
    if ($emptyDrawer) {
      $emptyDrawer.classList.add('hidden');
    }
    
    // Show the cart-drawer-items section and remove is-empty class
    const $drawerItems = this.querySelector('cart-drawer-items');
    if ($drawerItems) {
      $drawerItems.classList.remove('is-empty');
      // Remove is-empty from cart-drawer element itself to prevent CSS rules from affecting layout
      this.classList.remove('is-empty');
      const $inner = $drawerItems.querySelector('.js-drawer-inner');
      if ($inner) {
        // Insert loading screen (same as in cart-drawer-epl.liquid lines 362-368)
        $inner.innerHTML = `
          <div class="drawer__cart-items-wrapper js-cart-items-body">
            <div class="epl-cart-summary" style="min-height: 200px;">
              <div style="text-align: center; padding: 40px; color: #666;">
                <p>Laster handlekurv...</p>
              </div>
            </div>
          </div>
        `;
      }
    }
  }

  updateItems(newItems = null, preserveKey = null, shouldOpen = true, retryCount = 0) { 
    // Prevent infinite retry loops
    if (retryCount > 2) {
      console.error('Cart drawer update failed after multiple retries');
      return;
    }
    
    // Check if this is the EPL cart drawer by looking for the epl-cart-drawer-wrapper class
    const isEplDrawer = this.classList.contains('epl-cart-drawer-wrapper');
    const viewName = isEplDrawer ? 'cart-drawer-epl-items' : 'cart-items';
    
    // Determine the key of the most recently added main product
    let latestItemKey = preserveKey; // Use preserved key if provided
    let latestItemProductId = null;
    let latestItemSku = null;
    if (!latestItemKey && newItems && Array.isArray(newItems) && newItems.length > 0) {
      
      // Find the main product (not AppleCare, Secondary Warranty, or GWP)
      // Check multiple ways since the structure might vary
      const mainProduct = newItems.find(item => {
        if (!item) return false;
        const props = item.properties || {};
        const productType = item.product_type || item.product_type_name || '';
        
        // Main product should not have these properties and should not be these types
        // Also check product_type_name as fallback
        const productTypeName = item.product_type_name || item.product_type || '';
        const isMainProduct = !props._aplcr_main_product_sku && 
               !props._sw_main_product_sku && 
               !props._gwp_main_product_sku &&
               productType !== 'AppleCare' &&
               productType !== 'SecondWarranty' &&
               productTypeName !== 'AppleCare' &&
               productTypeName !== 'SecondWarranty' &&
               item.product_id && // Ensure it has a product_id
               !item.key.includes('applecare') && // Additional safety check
               !item.key.includes('warranty'); // Additional safety check
        
        return isMainProduct;
      });
      
      if (mainProduct) {
        latestItemKey = mainProduct.key;
        latestItemProductId = mainProduct.product_id || null;
        latestItemSku = mainProduct.sku || null;
      } else if (newItems.length > 0) {
        // Fallback: use the last item in the array (usually the main product is added last)
        const fallbackItem = newItems[newItems.length - 1];
        latestItemKey = fallbackItem.key;
        latestItemProductId = fallbackItem.product_id || null;
        latestItemSku = fallbackItem.sku || null;
      }
    }
    
    // Add cache-busting parameter to ensure fresh cart data
    const cacheBuster = `&_t=${Date.now()}`;
    const latestProductIdParam = latestItemProductId ? `&latest_product_id=${encodeURIComponent(latestItemProductId)}` : '';
    const latestSkuParam = latestItemSku ? `&latest_sku=${encodeURIComponent(latestItemSku)}` : '';
    const url = latestItemKey 
      ? `${window.Shopify.routes.root}cart?view=${viewName}&latest_key=${encodeURIComponent(latestItemKey)}${latestProductIdParam}${latestSkuParam}${cacheBuster}`
      : `${window.Shopify.routes.root}cart?view=${viewName}${latestProductIdParam}${latestSkuParam}${cacheBuster}`;
    
    // Check if loading screen was already prepared in open() method
    // If cart-drawer doesn't have is-empty but we have a loading screen, it was prepared
    const $drawerItems = this.querySelector('cart-drawer-items');
    const $existingLoadingScreen = $drawerItems?.querySelector('.js-cart-items-body .epl-cart-summary');
    const loadingScreenAlreadyPrepared = !!$existingLoadingScreen;
    
    // Show loading screen when transitioning from empty to non-empty cart
    // Check if empty drawer exists (even if hidden) to detect empty cart transition
    // Only prepare if it wasn't already prepared in open() method
    const $emptyDrawer = this.querySelector('.js-empty-drawer');
    const wasEmpty = this.classList.contains('is-empty') || ($emptyDrawer && !$emptyDrawer.classList.contains('hidden'));
    if (wasEmpty && !loadingScreenAlreadyPrepared) {
      this.prepareLoadingScreen();
    }
    
    // Open drawer immediately if requested, AFTER preparing loading screen
    // This ensures the loading screen is visible as soon as the drawer opens
    // Check if drawer is already open to avoid unnecessary work
    const isAlreadyOpen = this.classList.contains('active');
    if (shouldOpen && !this._isOpening && !isAlreadyOpen) {
      // Mark that we're opening to prevent refresh loop in open() method
      this._isOpening = true;
      this.open();
      this._isOpening = false;
    }
    
    const loadingMarkup = `
      <div class="epl-cart-summary" style="min-height: 200px;">
        <div style="text-align: center; padding: 40px; color: #666;">
          <p>Laster handlekurv...</p>
        </div>
      </div>
    `;
    
    const scheduleNonEmptyLoading = () => {
      if (!isEplDrawer || wasEmpty) return;
      if (this._pendingNonEmptyUpdate) return;
      this._pendingNonEmptyUpdate = true;
      const $itemsImmediate = this.querySelector('.js-cart-items-body');
      const isItemsEmpty = $itemsImmediate && $itemsImmediate.textContent.trim().length === 0;
      if (this.classList.contains('active') && isItemsEmpty) {
        $itemsImmediate.innerHTML = loadingMarkup;
        return;
      }
      if (this._loadingRaf1) cancelAnimationFrame(this._loadingRaf1);
      if (this._loadingRaf2) cancelAnimationFrame(this._loadingRaf2);
      this._loadingRaf1 = requestAnimationFrame(() => {
        this._loadingRaf2 = requestAnimationFrame(() => {
          if (!this._pendingNonEmptyUpdate) return;
          if (!this.classList.contains('active')) return;
          const $items = this.querySelector('.js-cart-items-body');
          if (!$items) return;
          const isAlreadyLoading = $items.querySelector('.epl-cart-summary')?.textContent.includes('Laster handlekurv');
          if (!isAlreadyLoading) {
            $items.innerHTML = loadingMarkup;
          }
        });
      });
    };
    
    const clearNonEmptyLoading = () => {
      this._pendingNonEmptyUpdate = false;
      if (this._loadingRaf1) cancelAnimationFrame(this._loadingRaf1);
      if (this._loadingRaf2) cancelAnimationFrame(this._loadingRaf2);
      this._loadingRaf1 = null;
      this._loadingRaf2 = null;
    };
    
    const attributeUpdatePromise = latestItemKey
      ? fetch(`${window.Shopify.routes.root}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            attributes: {
              _latest_key: latestItemKey,
              _latest_product_id: latestItemProductId || '',
              _latest_sku: latestItemSku || ''
            }
          })
        }).then(response => response.ok).catch(() => false)
      : Promise.resolve(false);

    scheduleNonEmptyLoading();
    
    // Fetch immediately without setTimeout wrapper for faster response
    attributeUpdatePromise.then(() => fetch(url))
      .then((response) => {
      if (!response.ok) {
        throw new Error(`Failed to fetch cart template: ${response.status} ${response.statusText}`);
      }
      return response.text();
    })
    .then((html) => { 
        // Parse HTML to extract just the inner content (avoid duplicate wrappers)
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const wrapperFromTemplate = doc.querySelector('.js-cart-items-body');
        const innerContent = wrapperFromTemplate ? wrapperFromTemplate.innerHTML : html;
        const templateMainItem = doc.querySelector('.epl-main-item');
        const templateItemKey = templateMainItem?.getAttribute('data-item-key') || null;
        const templateDisplayTitle = templateMainItem?.getAttribute('data-product-title') || null;
      
      const $items = this.querySelector('.js-cart-items-body') || null;

      if (wasEmpty) { 
        const $emptyDrawer = this.querySelector('.js-empty-drawer') || null;
        const $drawerItems = this.querySelector('cart-drawer-items') || null;

        if ($drawerItems) { 
          const $inner = $drawerItems.querySelector('.js-drawer-inner'); 

          if ($inner) { 
            // Parse the HTML to get the cart items body
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            const wrapperFromTemplate = doc.querySelector('.js-cart-items-body');
            
            // The template returns the wrapper div directly
            // When cart is empty, inner contains empty state, so we replace it with the wrapper
            if (wrapperFromTemplate) {
              // Replace inner content with the wrapper
              $inner.innerHTML = wrapperFromTemplate.outerHTML;
            } else {
              // Fallback: use the HTML as-is (might be the wrapper itself)
              $inner.innerHTML = html;
            }
            
            const $newItems = $inner.querySelector('.js-cart-items-body') || null;
            
            if ($newItems) { 
              // Update totals (item count and price) from data attributes
              this.updateDrawerTotals($newItems);
              if ($emptyDrawer) {
                $emptyDrawer.classList.add('hidden');
              }
              $drawerItems.classList.remove('is-empty');
              this.classList.remove('is-empty');

              // Re-initialize handlers after content update
              this._acHandlersInitialized = false;
              this.initAppleCareHandlers();
              this._swHandlersInitialized = false;
              this.initSecondWarrantyHandlers();
              this.initRecommendedAccessoriesHandlers();

              // Mark that we just updated to prevent refresh loop when opening
              this._justUpdated = true;
              
              if (shouldOpen && !this._isOpening) {
                this.open();
              }
              return; // Exit early since we've handled the empty case
            }
          }
        }
      }

      if ($items) {
        clearNonEmptyLoading();
        // Use inner content to avoid duplicate wrapper divs
        // Also preserve data attributes from the template wrapper if they exist
        if (wrapperFromTemplate) {
          // Copy data attributes from template wrapper to existing wrapper
          Array.from(wrapperFromTemplate.attributes).forEach(attr => {
            if (attr.name.startsWith('data-')) {
              $items.setAttribute(attr.name, attr.value);
            }
          });
        }
        
        // Check if innerContent is actually populated
        if (!innerContent || innerContent.trim().length === 0) {
          clearNonEmptyLoading();
          // Check if cart is actually empty by fetching cart.js
          fetch(`${window.Shopify.routes.root}cart.js`)
            .then(response => response.json())
            .then(cart => {
              if (!cart.items || cart.items.length === 0) {
                // Cart is actually empty, mark drawer as empty
                this.classList.add('is-empty');
                const $drawerItems = this.querySelector('cart-drawer-items');
                if ($drawerItems) {
                  $drawerItems.classList.add('is-empty');
                }
                const $emptyDrawer = this.querySelector('.js-empty-drawer');
                if ($emptyDrawer) {
                  $emptyDrawer.classList.remove('hidden');
                }
                return;
              }
              
              // Cart has items but template returned empty - retry after delay
              setTimeout(() => {
                this.updateItems(null, preserveKey, shouldOpen, retryCount + 1);
              }, 500);
            })
            .catch(error => {
              console.error('Error checking cart state:', error);
              // Retry update as fallback
              setTimeout(() => {
                this.updateItems(null, preserveKey, shouldOpen, retryCount + 1);
              }, 500);
            });
          return;
        }
        
        $items.innerHTML = innerContent;
        
        
        // Update totals (item count and price) from data attributes
        this.updateDrawerTotals($items);
        
        // Mark that we just updated to prevent refresh loop when opening
        this._justUpdated = true;
        
        // Re-initialize AC+ event listeners after content update
        // Reset the flag to allow re-initialization with new content
        this._acHandlersInitialized = false;
        this.initAppleCareHandlers();
        
        // Re-initialize SW handlers after content update
        this._swHandlersInitialized = false;
        this.initSecondWarrantyHandlers();
        
        // Re-initialize recommended accessories handlers
        this.initRecommendedAccessoriesHandlers();
        
        // If preserveKey is provided, immediately fix the display before any other logic
        // This ensures the correct main product is shown even if template selected wrong item
        if (preserveKey) {
          // Fetch cart and fix display immediately
          fetch(`${window.Shopify.routes.root}cart.js`)
            .then(response => response.json())
            .then(cart => {
              const correctItem = cart.items.find(item => item.key === preserveKey);
              const mainItem = $items.querySelector('.epl-main-item');
              
              if (correctItem && mainItem) {
                
                // Update image
                const img = mainItem.querySelector('.epl-main-item__image img');
                if (img && correctItem.image) {
                  img.src = correctItem.image;
                  img.alt = correctItem.image_alt || correctItem.title;
                }
                
                // Update title and data attribute from the template display title
                const titleEl = mainItem.querySelector('.epl-main-item__title');
                if (titleEl) {
                  const fallbackTitle = correctItem.product_title || correctItem.title;
                  const displayTitle = (templateItemKey === correctItem.key && templateDisplayTitle)
                    ? templateDisplayTitle
                    : fallbackTitle;
                  if (displayTitle) {
                    mainItem.setAttribute('data-product-title', displayTitle);
                    titleEl.textContent = displayTitle;
                  }
                }
                
                // Update price (unit price, not line total)
                const priceEl = mainItem.querySelector('.epl-main-item__price');
                if (priceEl) {
                  const priceInCents = correctItem.final_price || correctItem.price || 0;
                  let price = priceInCents / 100;
                  const isB2B = (typeof order_type !== 'undefined' && order_type === 'b2b') || window.location.host === 'uio.eplehuset.no';
                  if (isB2B) price = price / 1.25;
                  const formattedPrice = new Intl.NumberFormat('nb-NO', {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0
                  }).format(price).replace(/\u00A0/g, ' ');
                  if (isB2B) {
                    priceEl.innerHTML = `${formattedPrice} kr <span class="exvat-text">eks. mva.</span>`;
                    priceEl.classList.add('exvat-processed');
                  } else {
                    priceEl.textContent = `${formattedPrice} kr`;
                  }
                }

                // Update meta
                const metaEl = mainItem.querySelector('.epl-main-item__meta');
                if (metaEl) {
                  let metaText = '';
                  if (correctItem.variant_title && correctItem.variant_title !== 'Default Title') {
                    metaText = correctItem.variant_title;
                  }
                  if (correctItem.quantity && correctItem.quantity > 1) {
                    if (metaText) metaText += ' · ';
                    metaText += `Qty ${correctItem.quantity}`;
                  }
                  metaEl.textContent = metaText;
                }

                // Update item key attribute
                mainItem.setAttribute('data-item-key', correctItem.key);
              }
            })
            .catch(error => {
              console.error('Error in immediate fix:', error);
            });
        }
        
        const mainItem = $items.querySelector('.epl-main-item');
        
        // If we have a latest item key, verify and fix the displayed item
        if (latestItemKey) {
          const displayedItemKey = mainItem ? mainItem.getAttribute('data-item-key') : null;
          
          // If the displayed item doesn't match, fetch cart JSON and update
          // ALWAYS fix the display when preserveKey is provided to ensure correct item is shown
          if (displayedItemKey !== latestItemKey || preserveKey) {
            
            // Fetch cart JSON to get the correct item data
            fetch(`${window.Shopify.routes.root}cart.js`)
              .then(response => response.json())
              .then(cart => {
                // Find the item with the matching key
                const correctItem = cart.items.find(item => item.key === latestItemKey);
                
                if (correctItem && mainItem) {
                  
                  // Update the image
                  const img = mainItem.querySelector('.epl-main-item__image img');
                  if (img && correctItem.image) {
                    img.src = correctItem.image;
                    img.alt = correctItem.image_alt || correctItem.title;
                  }
                  
                  // Update the title and data attribute from the template display title
                  const titleEl = mainItem.querySelector('.epl-main-item__title');
                  if (titleEl) {
                    const fallbackTitle = correctItem.product_title || correctItem.title;
                    const displayTitle = (templateItemKey === correctItem.key && templateDisplayTitle)
                      ? templateDisplayTitle
                      : fallbackTitle;
                    if (displayTitle) {
                      mainItem.setAttribute('data-product-title', displayTitle);
                      titleEl.textContent = displayTitle;
                    }
                  }
                  
                  // Update the price (unit price; cart.js returns prices in cents)
                  const priceEl = mainItem.querySelector('.epl-main-item__price');
                  if (priceEl) {
                    const priceInCents = correctItem.final_price || correctItem.price || 0;
                    let price = priceInCents / 100;
                    const isB2B = (typeof order_type !== 'undefined' && order_type === 'b2b') || window.location.host === 'uio.eplehuset.no';
                    if (isB2B) price = price / 1.25;
                    const formattedPrice = new Intl.NumberFormat('nb-NO', {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0
                    }).format(price).replace(/\u00A0/g, ' ');
                    if (isB2B) {
                      priceEl.innerHTML = `${formattedPrice} kr <span class="exvat-text">eks. mva.</span>`;
                      priceEl.classList.add('exvat-processed');
                    } else {
                      priceEl.textContent = `${formattedPrice} kr`;
                    }
                  }
                  
                  // Update variant title and quantity in meta
                  const metaEl = mainItem.querySelector('.epl-main-item__meta');
                  if (metaEl) {
                    let metaText = '';
                    if (correctItem.variant_title && correctItem.variant_title !== 'Default Title') {
                      metaText = correctItem.variant_title;
                    }
                    if (correctItem.quantity && correctItem.quantity > 1) {
                      if (metaText) metaText += ' · ';
                      metaText += `Qty ${correctItem.quantity}`;
                    }
                    metaEl.textContent = metaText;
                  }
                  
                  // Update option fields if they exist (Option1Value, Option2Value)
                  // These are rendered server-side, so we'd need to fetch product data to update them
                  // For now, we'll just ensure the main display is correct
                  
                  // Update the item key attribute to reflect the correct item
                  mainItem.setAttribute('data-item-key', correctItem.key);
                }
              })
              .catch(error => {
                console.error('Error fetching cart JSON:', error);
              });
          }
        }
        
        // Only open the drawer if explicitly requested, not already opening, and not already open
        const isAlreadyOpen = this.classList.contains('active');
        if (shouldOpen && !this._isOpening && !isAlreadyOpen) {
          this.open();
        }
      } else {
        // If $items doesn't exist and cart is not empty, try to refresh by fetching cart again
        if (!wasEmpty) {
          // Force a full refresh by clearing and re-fetching
          setTimeout(() => {
            this.updateItems(null, null, false);
          }, 500);
        }
      }
    })
    .catch((error) => {
      clearNonEmptyLoading();
      console.error('Error updating cart drawer:', error);
      // If update fails, try to refresh the cart drawer after a delay
      setTimeout(() => {
        this.updateItems(null, null, false);
      }, 1000);
    });
  }

  addEvents() { 
    const $modalTriggers = this.querySelectorAll('.ac-modal-trigger') || [];

    if ($modalTriggers?.length > 0) { 
      [...$modalTriggers].forEach(($trigger) => {
        $trigger.addEventListener('click', (event) => {
          this.close();
        });
      });
    }
    
    // Initialize AC+ handlers
    this.initAppleCareHandlers();
    
    // Initialize SW handlers
    this.initSecondWarrantyHandlers();
    
    // Initialize recommended accessories handlers
    this.initRecommendedAccessoriesHandlers();
  }
  
  initAppleCareHandlers() {
    // Use event delegation on the cart drawer container so handlers persist after HTML updates
    const container = this.querySelector('.js-cart-items-body') || this;
    
    // Remove old listener if it exists (we'll store a reference to it)
    if (this._acClickHandler) {
      container.removeEventListener('click', this._acClickHandler);
    }
    
    // Create a new handler function and store it
    this._acClickHandler = (e) => {
      const addButton = e.target.closest('.add-cart-apple-care');
      if (addButton) {
        e.preventDefault();
        e.stopPropagation();
        this.handleAddAppleCare(addButton);
        return;
      }
      
      // Handle remove AC+ button
      const removeButton = e.target.closest('.epl-upsell__remove-button[data-action="remove"]');
      if (removeButton) {
        e.preventDefault();
        e.stopPropagation();
        this.handleRemoveAppleCare(removeButton);
        return;
      }
    };
    
    // Attach the handler
    container.addEventListener('click', this._acClickHandler);
    this._acHandlersInitialized = true;
  }
  
  async handleAddAppleCare(button) {
    // Check if another operation is in progress
    if (this._operationLock) {
      button.disabled = true;
      // Queue this operation - don't change text
      this._pendingOperations.push(() => this.handleAddAppleCare(button));
      return;
    }
    
    // Acquire lock
    this._operationLock = true;
    
    // Disable all buttons to prevent concurrent clicks
    this._disableAllButtons();
    
    // Disable button and show loading state
    button.disabled = true;
    button.textContent = 'Legger til...';
    const originalText = 'Legg til';
    
    try {
      // Extract data attributes
      const variantId = button.dataset.variantid;
      const mainProductSku = button.dataset.skuparent;
      const mainProductHandle = button.dataset.handleparent;
      const mainProductKey = button.dataset.keyparent;
      const uuid = button.dataset.uuid || mainProductHandle;
      const acSku = button.dataset.skuaplcr;
      const acHandle = button.dataset.handleaplcr;
      const mainProductIndex = parseInt(button.dataset.forproduct);
      
      // Get current cart to find the main product line
      const cartResponse = await fetch(`${window.Shopify.routes.root}cart.js`);
      const cart = await cartResponse.json();
      
      const mainProductItem = cart.items.find(item => item.key === mainProductKey);
      if (!mainProductItem) {
        throw new Error('Main product not found in cart');
      }
      
      const mainProductLine = cart.items.findIndex(item => item.key === mainProductKey) + 1;
      
      // Build UUID: append AC+ handle to existing UUID (which may already include SW handle)
      const currentUuid = mainProductItem.properties?._uuid || uuid;
      const mainProductUuid = currentUuid + '-' + acHandle;
      
      // Update main product with AC+ properties
      const updateMainProductData = {
        line: mainProductLine,
        quantity: mainProductItem.quantity,
        properties: {
          ...mainProductItem.properties,
          _applecare_sku: acSku,
          _applecare_handle: acHandle,
          _uuid: mainProductUuid
        }
      };
      
      // Update main product line and get the response to capture updated key
      const changeResponse = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateMainProductData)
      });
      
      const changeResult = await changeResponse.json();
      
      // Find the updated main product key from the change response
      // The key might have changed when properties were updated
      let updatedMainProductKey = mainProductKey;
      if (changeResult.items && changeResult.items.length > 0) {
        const updatedMainProduct = changeResult.items.find(item => 
          item.sku === mainProductSku && 
          !item.properties?._aplcr_main_product_sku &&
          !item.properties?._sw_main_product_sku &&
          !item.properties?._gwp_main_product_sku &&
          item.product_type !== 'AppleCare' &&
          item.product_type !== 'SecondWarranty'
        );
        if (updatedMainProduct) {
          updatedMainProductKey = updatedMainProduct.key;
        }
      }
      
      // If SW is already in cart, update its UUID to match the new main product UUID
      // This ensures AC+ and SW stay linked to the same main product
      const existingSwItem = changeResult.items?.find(item => 
        item.properties?._sw_main_product_sku === mainProductSku &&
        item.properties?._uuid !== mainProductUuid
      );
      
      if (existingSwItem) {
        const swLine = changeResult.items.findIndex(item => item.key === existingSwItem.key) + 1;
        await fetch(`${window.Shopify.routes.root}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            line: swLine,
            quantity: existingSwItem.quantity,
            properties: {
              ...existingSwItem.properties,
              _uuid: mainProductUuid
            }
          })
        });
      }
      
      // Add AC+ as separate line item
      const addAcData = {
        items: [{
          id: variantId,
          quantity: mainProductItem.quantity,
          properties: {
            _aplcr_main_product_sku: mainProductSku,
            _aplcr_main_product_handle: mainProductHandle,
            _uuid: mainProductUuid
          }
        }]
      };
      
      const addResponse = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(addAcData)
      });
      
      // Refresh cart drawer, preserving the main product key
      // Use the key we got from the change response
      this.updateItems(null, updatedMainProductKey);
    } catch (error) {
      console.error('Error adding AppleCare:', error);
      button.disabled = false;
      button.textContent = originalText;
      alert('Kunne ikke legge til AppleCare+. Prøv igjen.');
    } finally {
      // Release lock and process next operation
      this._operationLock = false;
      this._enableAllButtons();
      
      // Process next queued operation
      if (this._pendingOperations.length > 0) {
        const nextOp = this._pendingOperations.shift();
        setTimeout(() => nextOp(), 100); // Small delay to ensure cart state is stable
      }
    }
  }
  
  async handleRemoveAppleCare(button) {
    // Check if another operation is in progress
    if (this._operationLock) {
      button.disabled = true;
      // Queue this operation - don't change text
      this._pendingOperations.push(() => this.handleRemoveAppleCare(button));
      return;
    }
    
    // Acquire lock
    this._operationLock = true;
    
    // Disable all buttons to prevent concurrent clicks
    this._disableAllButtons();
    
    // Disable button - don't change text, just show disabled state
    button.disabled = true;
    const originalText = button.textContent;
    
    try {
      const acLineKey = button.dataset.acLineKey;
      const mainProductKey = button.dataset.mainProductKey;
      const mainProductIndex = parseInt(button.dataset.mainProductIndex);
      const mainProductUuid = button.dataset.mainProductUuid;
      
      // Get current cart
      const cartResponse = await fetch(`${window.Shopify.routes.root}cart.js`);
      const cart = await cartResponse.json();
      
      // Find AC+ line item
      const acLineItem = cart.items.find(item => item.key === acLineKey);
      if (!acLineItem) {
        // Item doesn't exist - cart may be in inconsistent state, refresh drawer
        this.updateItems(null, mainProductKey);
        button.disabled = false;
        button.textContent = originalText;
        return;
      }
      
      const acLine = cart.items.findIndex(item => item.key === acLineKey) + 1;
      
      // Remove AC+ line item
      await fetch(`${window.Shopify.routes.root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          line: acLine,
          quantity: 0
        })
      });
      
      // Update main product to remove AC+ properties
      const mainProductItem = cart.items.find(item => item.key === mainProductKey);
      let updatedMainProductKey = mainProductKey;
      
      if (mainProductItem) {
        const mainProductLine = cart.items.findIndex(item => item.key === mainProductKey) + 1;
        const updatedProperties = { ...mainProductItem.properties };
        const acHandle = updatedProperties._applecare_handle;
        delete updatedProperties._applecare_sku;
        delete updatedProperties._applecare_handle;
        // Restore original UUID (remove AC+ handle suffix)
        if (updatedProperties._uuid && acHandle && updatedProperties._uuid.endsWith('-' + acHandle)) {
          updatedProperties._uuid = updatedProperties._uuid.replace('-' + acHandle, '');
        } else if (updatedProperties._uuid && mainProductUuid) {
          // Fallback: use the provided UUID if it doesn't match the pattern
          updatedProperties._uuid = mainProductUuid;
        }
        
        // Update main product and get response to capture updated key
        const changeResponse = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            line: mainProductLine,
            quantity: mainProductItem.quantity,
            properties: updatedProperties
          })
        });
        
        const changeResult = await changeResponse.json();
        
        // Find the updated main product key from the change response
        if (changeResult.items && changeResult.items.length > 0) {
          const updatedMainProduct = changeResult.items.find(item => 
            item.sku === mainProductItem.sku && 
            !item.properties?._aplcr_main_product_sku &&
            !item.properties?._sw_main_product_sku &&
            !item.properties?._gwp_main_product_sku &&
            item.product_type !== 'AppleCare' &&
            item.product_type !== 'SecondWarranty'
          );
          if (updatedMainProduct) {
            updatedMainProductKey = updatedMainProduct.key;
          }
        }
      }
      
      // Refresh cart drawer, preserving the main product key
      // Add a small delay to ensure server has processed the removal
      setTimeout(() => {
        this.updateItems(null, updatedMainProductKey, false);
      }, 300);
    } catch (error) {
      console.error('Error removing AppleCare:', error);
      button.disabled = false;
      button.textContent = originalText;
      alert('Kunne ikke fjerne AppleCare+. Prøv igjen.');
    } finally {
      // Release lock and process next operation
      this._operationLock = false;
      this._enableAllButtons();
      
      // Process next queued operation
      if (this._pendingOperations.length > 0) {
        const nextOp = this._pendingOperations.shift();
        setTimeout(() => nextOp(), 100); // Small delay to ensure cart state is stable
      }
    }
  }
  
  async handleAddSecondWarranty(button) {
    // Check if another operation is in progress
    if (this._operationLock) {
      button.disabled = true;
      // Queue this operation - don't change text
      this._pendingOperations.push(() => this.handleAddSecondWarranty(button));
      return;
    }
    
    // Acquire lock
    this._operationLock = true;
    
    // Disable all buttons to prevent concurrent clicks
    this._disableAllButtons();
    
    // Disable button and show loading state
    button.disabled = true;
    button.textContent = 'Legger til...';
    const originalText = 'Legg til';
    
    try {
      // Extract data attributes
      const variantId = button.dataset.variantid;
      const mainProductSku = button.dataset.skuparent;
      const mainProductHandle = button.dataset.handleparent;
      const mainProductKey = button.dataset.keyparent;
      const uuid = button.dataset.uuid || mainProductHandle;
      const swSku = button.dataset.skusw;
      const swHandle = button.dataset.handlesw;
      const mainProductIndex = parseInt(button.dataset.forproduct);
      
      // Get current cart to find the main product line
      const cartResponse = await fetch(`${window.Shopify.routes.root}cart.js`);
      const cart = await cartResponse.json();
      
      const mainProductItem = cart.items.find(item => item.key === mainProductKey);
      if (!mainProductItem) {
        throw new Error('Main product not found in cart');
      }
      
      const mainProductLine = cart.items.findIndex(item => item.key === mainProductKey) + 1;
      
      // Build UUID: append SW handle to existing UUID (which may already include AC+ handle)
      const currentUuid = mainProductItem.properties?._uuid || uuid;
      const mainProductUuid = currentUuid + '-' + swHandle;
      
      // Update main product with SW properties
      const updateMainProductData = {
        line: mainProductLine,
        quantity: mainProductItem.quantity,
        properties: {
          ...mainProductItem.properties,
          _secWarranty_sku: swSku,
          _secWarranty_handle: swHandle,
          _uuid: mainProductUuid
        }
      };
      
      // Update main product line and get the response to capture updated key
      const changeResponse = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateMainProductData)
      });
      
      const changeResult = await changeResponse.json();
      
      // Find the updated main product key from the change response
      // The key might have changed when properties were updated
      let updatedMainProductKey = mainProductKey;
      if (changeResult.items && changeResult.items.length > 0) {
        const updatedMainProduct = changeResult.items.find(item => 
          item.sku === mainProductSku && 
          !item.properties?._aplcr_main_product_sku &&
          !item.properties?._sw_main_product_sku &&
          !item.properties?._gwp_main_product_sku &&
          item.product_type !== 'AppleCare' &&
          item.product_type !== 'SecondWarranty'
        );
        if (updatedMainProduct) {
          updatedMainProductKey = updatedMainProduct.key;
        }
      }
      
      // If AC+ is already in cart, update its UUID to match the new main product UUID
      // This ensures AC+ and SW stay linked to the same main product
      const existingAcItem = changeResult.items?.find(item => 
        item.properties?._aplcr_main_product_sku === mainProductSku &&
        item.properties?._uuid !== mainProductUuid
      );
      
      if (existingAcItem) {
        const acLine = changeResult.items.findIndex(item => item.key === existingAcItem.key) + 1;
        await fetch(`${window.Shopify.routes.root}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            line: acLine,
            quantity: existingAcItem.quantity,
            properties: {
              ...existingAcItem.properties,
              _uuid: mainProductUuid
            }
          })
        });
      }
      
      // Add SW as separate line item
      const addSwData = {
        items: [{
          id: variantId,
          quantity: mainProductItem.quantity,
          properties: {
            _sw_main_product_sku: mainProductSku,
            _sw_main_product_handle: mainProductHandle,
            _uuid: mainProductUuid
          }
        }]
      };
      
      const addResponse = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(addSwData)
      });
      
      if (!addResponse.ok) {
        const errorData = await addResponse.json();
        throw new Error(errorData.message || 'Failed to add Second Warranty to cart');
      }
      
      const addResult = await addResponse.json();
      
      // Refresh cart drawer, preserving the main product key
      // Use the key we got from the change response
      // Pass the new items so updateItems can properly detect the main product
      this.updateItems(addResult.items || null, updatedMainProductKey, true);
    } catch (error) {
      console.error('Error adding Second Warranty:', error);
      button.disabled = false;
      button.textContent = originalText;
      alert('Kunne ikke legge til Second Warranty. Prøv igjen.');
    } finally {
      // Release lock and process next operation
      this._operationLock = false;
      this._enableAllButtons();
      
      // Process next queued operation
      if (this._pendingOperations.length > 0) {
        const nextOp = this._pendingOperations.shift();
        setTimeout(() => nextOp(), 100); // Small delay to ensure cart state is stable
      }
    }
  }
  
  async handleRemoveSecondWarranty(button) {
    // Check if another operation is in progress
    if (this._operationLock) {
      button.disabled = true;
      // Queue this operation - don't change text
      this._pendingOperations.push(() => this.handleRemoveSecondWarranty(button));
      return;
    }
    
    // Acquire lock
    this._operationLock = true;
    
    // Disable all buttons to prevent concurrent clicks
    this._disableAllButtons();
    
    // Disable button - don't change text, just show disabled state
    button.disabled = true;
    const originalText = button.textContent;
    
    try {
      const swLineKey = button.dataset.swLineKey;
      const mainProductKey = button.dataset.mainProductKey;
      const mainProductIndex = parseInt(button.dataset.mainProductIndex);
      const mainProductUuid = button.dataset.mainProductUuid;
      
      // Get current cart
      const cartResponse = await fetch(`${window.Shopify.routes.root}cart.js`);
      const cart = await cartResponse.json();
      
      // Find SW line item
      const swLineItem = cart.items.find(item => item.key === swLineKey);
      if (!swLineItem) {
        // Item doesn't exist - cart may be in inconsistent state, refresh drawer
        this.updateItems(null, mainProductKey);
        button.disabled = false;
        button.textContent = originalText;
        return;
      }
      
      const swLine = cart.items.findIndex(item => item.key === swLineKey) + 1;
      
      // Remove SW line item
      await fetch(`${window.Shopify.routes.root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          line: swLine,
          quantity: 0
        })
      });
      
      // Update main product to remove SW properties
      const mainProductItem = cart.items.find(item => item.key === mainProductKey);
      let updatedMainProductKey = mainProductKey;
      
      if (mainProductItem) {
        const mainProductLine = cart.items.findIndex(item => item.key === mainProductKey) + 1;
        const updatedProperties = { ...mainProductItem.properties };
        const swHandle = updatedProperties._secWarranty_handle;
        delete updatedProperties._secWarranty_sku;
        delete updatedProperties._secWarranty_handle;
        // Restore UUID by removing SW handle suffix (but preserve AC+ if present)
        if (updatedProperties._uuid && swHandle) {
          // Remove the SW handle from UUID, but keep any AC+ handle that might be there
          const uuidParts = updatedProperties._uuid.split('-' + swHandle);
          if (uuidParts.length > 1) {
            updatedProperties._uuid = uuidParts[0];
          } else if (mainProductUuid) {
            // Fallback: use the provided UUID if pattern doesn't match
            updatedProperties._uuid = mainProductUuid;
          }
        } else if (updatedProperties._uuid && mainProductUuid) {
          // Fallback: use the provided UUID if it doesn't match the pattern
          updatedProperties._uuid = mainProductUuid;
        }
        
        // Update main product and get response to capture updated key
        const changeResponse = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            line: mainProductLine,
            quantity: mainProductItem.quantity,
            properties: updatedProperties
          })
        });
        
        const changeResult = await changeResponse.json();
        
        // Find the updated main product key from the change response
        if (changeResult.items && changeResult.items.length > 0) {
          const updatedMainProduct = changeResult.items.find(item => 
            item.sku === mainProductItem.sku && 
            !item.properties?._aplcr_main_product_sku &&
            !item.properties?._sw_main_product_sku &&
            !item.properties?._gwp_main_product_sku &&
            item.product_type !== 'AppleCare' &&
            item.product_type !== 'SecondWarranty'
          );
          if (updatedMainProduct) {
            updatedMainProductKey = updatedMainProduct.key;
          }
        }
      }
      
      // Refresh cart drawer, preserving the main product key
      // Add a small delay to ensure server has processed the removal
      setTimeout(() => {
        this.updateItems(null, updatedMainProductKey, false);
      }, 300);
    } catch (error) {
      console.error('Error removing Second Warranty:', error);
      button.disabled = false;
      button.textContent = originalText;
      alert('Kunne ikke fjerne Second Warranty. Prøv igjen.');
    } finally {
      // Release lock and process next operation
      this._operationLock = false;
      this._enableAllButtons();
      
      // Process next queued operation
      if (this._pendingOperations.length > 0) {
        const nextOp = this._pendingOperations.shift();
        setTimeout(() => nextOp(), 100); // Small delay to ensure cart state is stable
      }
    }
  }
  
  async handleRemoveVegglader(button) {
    // Check if another operation is in progress
    if (this._operationLock) {
      button.disabled = true;
      // Queue this operation - don't change text
      this._pendingOperations.push(() => this.handleRemoveVegglader(button));
      return;
    }
    
    // Acquire lock
    this._operationLock = true;
    
    // Disable all buttons to prevent concurrent clicks
    this._disableAllButtons();
    
    // Disable button - don't change text, just show disabled state
    button.disabled = true;
    const originalText = button.textContent;
    
    try {
      const veggladerLineKey = button.dataset.veggladerLineKey;
      const mainProductKey = button.dataset.mainProductKey;
      const mainProductIndex = parseInt(button.dataset.mainProductIndex);
      const mainProductUuid = button.dataset.mainProductUuid;
      
      // Get current cart
      const cartResponse = await fetch(`${window.Shopify.routes.root}cart.js`);
      const cart = await cartResponse.json();
      
      // Find Vegglader line item
      const veggladerLineItem = cart.items.find(item => item.key === veggladerLineKey);
      if (!veggladerLineItem) {
        // Item doesn't exist - cart may be in inconsistent state, refresh drawer
        this.updateItems(null, mainProductKey);
        button.disabled = false;
        button.textContent = originalText;
        return;
      }
      
      const veggladerLine = cart.items.findIndex(item => item.key === veggladerLineKey) + 1;
      
      // Remove Vegglader line item
      await fetch(`${window.Shopify.routes.root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          line: veggladerLine,
          quantity: 0
        })
      });
      
      // Refresh cart drawer, preserving the main product key
      // Add a small delay to ensure server has processed the removal
      setTimeout(() => {
        this.updateItems(null, mainProductKey, false);
      }, 300);
    } catch (error) {
      console.error('Error removing Vegglader:', error);
      button.disabled = false;
      button.textContent = originalText;
      alert('Kunne ikke fjerne Vegglader. Prøv igjen.');
    } finally {
      // Release lock and process next operation
      this._operationLock = false;
      this._enableAllButtons();
      
      // Process next queued operation
      if (this._pendingOperations.length > 0) {
        const nextOp = this._pendingOperations.shift();
        setTimeout(() => nextOp(), 100); // Small delay to ensure cart state is stable
      }
    }
  }
  
  initSecondWarrantyHandlers() {
    // Use event delegation on the cart drawer container so handlers persist after HTML updates
    const container = this.querySelector('.js-cart-items-body') || this;
    
    // Remove old listener if it exists (we'll store a reference to it)
    if (this._swClickHandler) {
      container.removeEventListener('click', this._swClickHandler);
    }
    
    // Create a new handler function and store it
    this._swClickHandler = (e) => {
      const addButton = e.target.closest('.add-cart-second-warranty');
      if (addButton) {
        e.preventDefault();
        e.stopPropagation();
        this.handleAddSecondWarranty(addButton);
        return;
      }
      
      // Handle remove SW button
      const removeButton = e.target.closest('.epl-upsell__remove-button[data-action="remove-sw"]');
      if (removeButton) {
        e.preventDefault();
        e.stopPropagation();
        this.handleRemoveSecondWarranty(removeButton);
        return;
      }
    };
    
    // Attach the handler
    container.addEventListener('click', this._swClickHandler);
    this._swHandlersInitialized = true;
  }
  
  initRecommendedAccessoriesHandlers() {
    // Always use 'this' (cart-drawer element) for event delegation
    // This ensures clicks work in both empty cart (.js-empty-drawer bestsellers) 
    // and non-empty cart (.js-cart-items-body recommended accessories) sections
    const container = this;
    
    // Remove old listener from the container it was attached to
    if (this._recommendedAccessoriesClickHandler && this._recommendedAccessoriesContainer) {
      this._recommendedAccessoriesContainer.removeEventListener('click', this._recommendedAccessoriesClickHandler);
    }
    
    // Store reference to container for proper cleanup later
    this._recommendedAccessoriesContainer = container;
    
    // Create a new handler function and store it
    this._recommendedAccessoriesClickHandler = (e) => {
      // Check if clicked element is an upsell button (but not AppleCare or Second Warranty buttons)
      let button = e.target.closest('.epl-upsell__product-button');
      
      // Also check for .slider-form buttons within .epl-recommended section (from card-product-slider)
      if (!button) {
        const sliderFormButton = e.target.closest('.slider-form');
        if (sliderFormButton && sliderFormButton.closest('.epl-recommended')) {
          button = sliderFormButton;
        }
      }
      
      // Also check for .epl-cart-drawer-atc-button buttons (from card-product-cart-drawer)
      if (!button) {
        const cartDrawerButton = e.target.closest('.epl-cart-drawer-atc-button');
        if (cartDrawerButton && cartDrawerButton.closest('.epl-recommended')) {
          button = cartDrawerButton;
        }
      }
      
      // Handle Vegglader remove button
      if (button && button.classList.contains('epl-upsell__remove-button')) {
        const action = button.getAttribute('data-action');
        if (action === 'remove-vegglader') {
          e.preventDefault();
          e.stopPropagation();
          this.handleRemoveVegglader(button);
          return;
        }
        // Skip other remove buttons (AC+ and SW have their own handlers)
        if (action === 'remove' || action === 'remove-sw') {
          return;
        }
      }
      
      // Skip if it's an AppleCare or Second Warranty button (they have their own handlers)
      if (button && (
        button.classList.contains('add-cart-apple-care') ||
        button.classList.contains('add-cart-second-warranty')
      )) {
        return;
      }
      
      // Check if button has variant ID (supports data-variant-id, data-variantId, and data-variantid)
      // HTML data attributes with hyphens are accessible via camelCase in dataset
      let variantId = button?.dataset.variantId || button?.getAttribute('data-variant-id');
      
      // Also check for data-variantid (all lowercase, no hyphen) from slider-form buttons
      if (!variantId && button) {
        variantId = button.getAttribute('data-variantid');
      }
      
      if (button && variantId) {
        e.preventDefault();
        e.stopPropagation();
        
        // For slider-form buttons, ensure they have the expected data attributes for handleAddRecommendedAccessory
        if (button.classList.contains('slider-form')) {
          // Ensure data-variant-id exists (handleAddRecommendedAccessory expects this)
          if (!button.getAttribute('data-variant-id') && variantId) {
            button.setAttribute('data-variant-id', variantId);
          }
          
          // Ensure data-product-handle exists (extract from data-productHandle if needed)
          if (!button.getAttribute('data-product-handle')) {
            const productHandle = button.dataset.productHandle || button.getAttribute('data-productHandle');
            if (productHandle) {
              button.setAttribute('data-product-handle', productHandle);
            }
          }
        }
        
        this.handleAddRecommendedAccessory(button);
        return;
      }
    };
    
    // Attach the handler
    container.addEventListener('click', this._recommendedAccessoriesClickHandler);
    this._recommendedAccessoriesHandlersInitialized = true;
  }
  
  _disableAllButtons() {
    // Disable all AC+, SW add buttons, remove buttons (including Vegglader), and recommended accessory buttons
    // Don't change button text - just disable them visually
    const container = this.querySelector('.js-cart-items-body') || this;
    const acAddButtons = container.querySelectorAll('.add-cart-apple-care:not(:disabled)');
    const swAddButtons = container.querySelectorAll('.add-cart-second-warranty:not(:disabled)');
    // This selector includes all remove buttons: AC+, SW, and Vegglader (data-action="remove-vegglader")
    const removeButtons = container.querySelectorAll('.epl-upsell__remove-button:not(:disabled)');
    // Recommended accessory buttons (bestsellers and recommended accessories)
    const recommendedButtons = container.querySelectorAll('.epl-cart-drawer-atc-button:not(:disabled), .slider-form:not(:disabled)');
    
    this._disabledButtons = [];
    
    acAddButtons.forEach(btn => {
      if (!btn.disabled) {
        this._disabledButtons.push({ button: btn, originalText: btn.textContent, isAdd: true });
        btn.disabled = true;
        // Don't change text - just disable
      }
    });
    
    swAddButtons.forEach(btn => {
      if (!btn.disabled) {
        this._disabledButtons.push({ button: btn, originalText: btn.textContent, isAdd: true });
        btn.disabled = true;
        // Don't change text - just disable
      }
    });
    
    removeButtons.forEach(btn => {
      if (!btn.disabled) {
        this._disabledButtons.push({ button: btn, originalText: btn.textContent, isAdd: false });
        btn.disabled = true;
        // Don't change text - just disable
      }
    });
    
    recommendedButtons.forEach(btn => {
      if (!btn.disabled) {
        this._disabledButtons.push({ button: btn, originalText: btn.textContent, isAdd: true, isRecommended: true });
        btn.disabled = true;
        // Don't change text - just disable
      }
    });
  }
  
  _enableAllButtons() {
    // Re-enable all previously disabled buttons
    // For add buttons, DON'T restore text - let updateItems() handle the button state via HTML refresh
    // For remove buttons, the cart update will have already updated them, so we just re-enable
    if (this._disabledButtons) {
      this._disabledButtons.forEach(({ button, originalText, isAdd, isRecommended }) => {
        // Recommended accessory buttons - just re-enable, text will be updated by cart refresh
        if (isRecommended) {
          button.disabled = false;
        }
        // For AC/SW add buttons, don't restore text - the HTML will be refreshed by updateItems()
        // which will show the correct button state (either "Legg til" or "Fjern")
        // This prevents the button from briefly showing "Legg til" before the HTML update completes
        else if (isAdd && (button.classList.contains('add-cart-apple-care') || button.classList.contains('add-cart-second-warranty'))) {
          button.disabled = false;
          // Don't restore textContent - let updateItems() handle it via HTML refresh
        } else if (!isAdd && button.classList.contains('epl-upsell__remove-button')) {
          // Just re-enable remove buttons, text will be updated by cart refresh
          button.disabled = false;
        }
      });
      this._disabledButtons = [];
    }
  }
  
  /**
   * Updates the cart count badge without full drawer refresh
   * Fetches cart.js and updates the cart count bubble in the header
   * Also updates drawer totals if drawer is open
   */
  async updateCartCount() {
    try {
      const cartResponse = await fetch(`${window.Shopify.routes.root}cart.js`);
      const cart = await cartResponse.json();
      
      // Calculate total item count
      const itemCount = cart.items.reduce((total, item) => total + item.quantity, 0);
      
      // Update cart count bubble
      const cartCountBubble = document.querySelector('.cart-count-bubble span');
      const cartIconBubble = document.querySelector('#cart-icon-bubble');
      
      if (cartCountBubble) {
        cartCountBubble.textContent = itemCount;
      } else if (cartIconBubble && itemCount > 0) {
        // Create cart count bubble if it doesn't exist
        const bubble = document.createElement('div');
        bubble.className = 'cart-count-bubble';
        const span = document.createElement('span');
        span.textContent = itemCount;
        bubble.appendChild(span);
        cartIconBubble.appendChild(bubble);
      }
      
      // Also update drawer totals if drawer is open and this is the EPL drawer
      if (this.classList.contains('active') && this.classList.contains('epl-cart-drawer-wrapper')) {
        const $items = this.querySelector('.js-cart-items-body');
        if ($items) {
          // Update data attributes so updateDrawerTotals can use them
          const priceInCents = cart.total_price || 0;
          const price = priceInCents / 100;
          const formattedPrice = new Intl.NumberFormat('nb-NO', {
            style: 'currency',
            currency: cart.currency || 'NOK',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
          }).format(price);
          
          $items.setAttribute('data-cart-price', formattedPrice);
          $items.setAttribute('data-cart-item-count', cart.item_count || 0);
          
          // Update the totals display
          this.updateDrawerTotals($items);
        }
      }
    } catch (error) {
      console.error('Error updating cart count:', error);
      // Silently fail - cart count update is not critical
    }
  }
  
  /**
   * Updates the drawer totals (item count and total price) from data attributes
   * Falls back to fetching cart.js if data attributes aren't available
   * @param {HTMLElement} itemsContainer - The container element with data attributes (usually .js-cart-items-body)
   */
  updateDrawerTotals(itemsContainer) {
    const $total = this.querySelector('.js-drawer-total');
    const $itemCount = this.querySelector('.js-drawer-item-count');
    const isEplDrawer = this.classList.contains('epl-cart-drawer-wrapper');
    const $eplFooterCount = isEplDrawer ? this.querySelector('.drawer__footer--epl .count') : null;
    
    // Try to get values from data attributes first
    let cartPrice = null;
    let cartItemCount = null;
    
    if (itemsContainer) {
      const dataset = itemsContainer.dataset || {};
      cartPrice = dataset.cartPrice;
      // Check both camelCase (dataset) and direct attribute (for data-cart-item-count)
      cartItemCount = dataset.cartItemCount || itemsContainer.getAttribute('data-cart-item-count');
    }
    
    // If we have cartItemCount, update EPL footer count immediately (even without cartPrice)
    if (cartItemCount && $eplFooterCount) {
      $eplFooterCount.textContent = cartItemCount;
    }
    
    // If we have both values from data attributes, update immediately
    if (cartPrice && cartItemCount) {
      if ($total) {
        $total.textContent = cartPrice;
      }
      if ($itemCount) {
        const count = parseInt(cartItemCount, 10);
        const itemText = count === 1 ? 'vare' : 'varer';
        $itemCount.innerHTML = `${count} ${itemText} i <a href="${window.Shopify.routes.root}cart">handlekurven</a>`;
      }
      // EPL footer count already updated above
      return;
    }
    
    // If we only have cartItemCount (no cartPrice), update what we can and return
    if (cartItemCount && !cartPrice) {
      if ($itemCount) {
        const count = parseInt(cartItemCount, 10);
        const itemText = count === 1 ? 'vare' : 'varer';
        $itemCount.innerHTML = `${count} ${itemText} i <a href="${window.Shopify.routes.root}cart">handlekurven</a>`;
      }
      // EPL footer count already updated above
      return;
    }
    
    // Fallback: fetch from cart.js if data attributes aren't available
    if ((!cartPrice || !cartItemCount) && ($total || $itemCount || $eplFooterCount)) {
      fetch(`${window.Shopify.routes.root}cart.js`)
        .then(response => response.json())
        .then(cart => {
          // Update total price
          if ($total) {
            // Format price using Shopify's money format
            const priceInCents = cart.total_price || 0;
            const price = priceInCents / 100;
            // Use the same format as Shopify (with currency)
            const formattedPrice = new Intl.NumberFormat('nb-NO', {
              style: 'currency',
              currency: cart.currency || 'NOK',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0
            }).format(price);
            $total.textContent = formattedPrice;
          }
          
          // Update item count
          if ($itemCount) {
            const itemCount = cart.item_count || 0;
            const itemText = itemCount === 1 ? 'vare' : 'varer';
            $itemCount.textContent = `${itemCount} ${itemText}`;
          }
          
          // Update EPL footer count
          if ($eplFooterCount) {
            $eplFooterCount.textContent = cart.item_count || 0;
          }
        })
        .catch(error => {
          console.error('Error updating drawer totals:', error);
        });
    }
  }
  
  async handleAddRecommendedAccessory(button) {
    // Check if button is an anchor or button element
    const isAnchor = button.tagName === 'A';
    
    // Check if another operation is in progress
    if (this._operationLock) {
      // Disable button and queue this operation
      if (isAnchor) {
        button.style.pointerEvents = 'none';
        button.style.opacity = '0.6';
      } else {
        button.disabled = true;
      }
      this._pendingOperations.push(() => this.handleAddRecommendedAccessory(button));
      return;
    }
    
    // Acquire lock
    this._operationLock = true;
    
    // Disable all buttons to prevent concurrent clicks
    this._disableAllButtons();
    
    // Disable button and show loading state
    if (isAnchor) {
      button.style.pointerEvents = 'none';
      button.style.opacity = '0.6';
    } else {
      button.disabled = true;
    }
    const originalText = button.textContent;
    button.textContent = 'Legger til...';
    
    try {
      // Extract data attributes (supports data-variant-id, data-variantId, and data-variantid)
      let variantId = button.dataset.variantId || button.getAttribute('data-variant-id');
      if (!variantId) {
        variantId = button.getAttribute('data-variantid');
      }
      
      // Extract product handle (supports data-product-handle, data-productHandle)
      let productHandle = button.dataset.productHandle || button.getAttribute('data-product-handle');
      if (!productHandle) {
        productHandle = button.getAttribute('data-productHandle');
      }
      
      if (!variantId) {
        throw new Error('Variant ID mangler');
      }
      
      // Check if this is a Vegglader button (in the .epl-upsell--wide section)
      const isVegglader = button.closest('.epl-upsell--wide') !== null;
      const mainProductSku = button.dataset.mainProductSku || button.getAttribute('data-main-product-sku');
      const mainProductHandle = button.dataset.mainProductHandle || button.getAttribute('data-main-product-handle');
      const mainProductUuid = button.dataset.mainProductUuid || button.getAttribute('data-main-product-uuid');
      const mainProductKey = button.dataset.mainProductKey || button.getAttribute('data-main-product-key');
      
      // Build properties object
      const properties = {
        _uuid: mainProductUuid || productHandle || '',
        _recommended_accessory: 'true'
      };
      
      // If it's a Vegglader, add main product tracking properties
      if (isVegglader && mainProductSku && mainProductUuid) {
        properties._vegglader_main_product_sku = mainProductSku;
        properties._vegglader_main_product_handle = mainProductHandle || '';
        properties._vegglader_main_product_uuid = mainProductUuid;
        // Use main product UUID instead of productHandle for _uuid
        properties._uuid = mainProductUuid;
      }
      
      // Add accessory to cart
      const addResponse = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: parseInt(variantId),
            quantity: 1,
            properties: properties
          }]
        })
      });
      
      if (!addResponse.ok) {
        const errorData = await addResponse.json();
        throw new Error(errorData.message || 'Kunne ikke legge til produktet');
      }
      
      const addResult = await addResponse.json();
      
      // Refresh cart drawer to show remove button (button stays in "Legger til..." state until refresh)
      if (mainProductKey) {
        this.updateItems(null, mainProductKey);
      } else {
        // Full refresh to re-render recommended section with correct button states
        this.updateItems();
      }
    } catch (error) {
      console.error('Error adding recommended accessory:', error);
      if (isAnchor) {
        button.style.pointerEvents = '';
        button.style.opacity = '';
        button.style.cursor = '';
      } else {
        button.disabled = false;
      }
      button.textContent = originalText;
      alert('Kunne ikke legge til produktet. Prøv igjen.');
      
      // On error, do a full refresh to ensure cart state is correct
      const mainItem = this.querySelector('.epl-main-item');
      const currentMainProductKey = mainItem ? mainItem.getAttribute('data-item-key') : null;
      if (currentMainProductKey) {
        this.updateItems(null, currentMainProductKey);
      } else {
        this.updateItems();
      }
    } finally {
      // Release lock and process next operation
      this._operationLock = false;
      this._enableAllButtons();
      
      // Process next queued operation
      if (this._pendingOperations.length > 0) {
        const nextOp = this._pendingOperations.shift();
        setTimeout(() => nextOp(), 100); // Small delay to ensure cart state is stable
      }
    }
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
